import React from "react";
import { lighten, darken } from "./colorUtils";

const BUBBLES = [
  { x: 6,  y: 12, w: 24, h: 11, rx: 5, fill: "#F0BE39", delay: -2,   dur: 22, content: "text",   tail: "left"  },
  { x: 58, y: 20, w: 28, h: 12, rx: 6, fill: "#3EB567", delay: -7,   dur: 24, content: "typing", tail: "right" },
  { x: 16, y: 38, w: 22, h: 10, rx: 5, fill: "#FF8F47", delay: -12,  dur: 20, content: "text",   tail: "left"  },
  { x: 60, y: 48, w: 26, h: 11, rx: 5, fill: "#FFFFFF", delay: -4,   dur: 23, content: "text",   tail: "right" },
  { x: 10, y: 62, w: 30, h: 13, rx: 6, fill: "#F0BE39", delay: -16,  dur: 25, content: "typing", tail: "left"  },
  { x: 58, y: 72, w: 24, h: 10, rx: 5, fill: "#3EB567", delay: -9,   dur: 21, content: "text",   tail: "right" },
  { x: 30, y: 86, w: 20, h: 9,  rx: 4, fill: "#FFFFFF", delay: -14,  dur: 19, content: "typing", tail: "left"  },
];

const REACTIONS = [
  { x: 42, y: 18, kind: "heart", color: "#FF8F47", delay: -2,  dur: 7  },
  { x: 48, y: 42, kind: "star",  color: "#F0BE39", delay: -4,  dur: 9  },
  { x: 92, y: 32, kind: "heart", color: "#FFFFFF", delay: -1,  dur: 8  },
  { x: 4,  y: 50, kind: "star",  color: "#FFFFFF", delay: -3,  dur: 10 },
  { x: 52, y: 66, kind: "heart", color: "#F0BE39", delay: -6,  dur: 8  },
  { x: 88, y: 78, kind: "star",  color: "#3EB567", delay: -5,  dur: 9  },
];

const HEART_PATH = "M0,0 C-1.6,-1.8 -4,-1 -4,1 C-4,3 -1.5,4.5 0,5.5 C1.5,4.5 4,3 4,1 C4,-1 1.6,-1.8 0,0 Z";
const STAR_PATH  = "M0,-3 L0.9,-0.9 L3,-0.6 L1.4,0.8 L1.8,3 L0,1.9 L-1.8,3 L-1.4,0.8 L-3,-0.6 L-0.9,-0.9 Z";

export const SpeechBubblesBackground = ({ bgColor = "#3E8DFF" }) => {
  const topColor = lighten(bgColor, 0.3);
  const bottomColor = darken(bgColor, 0.4);
  const innerContent = (b) => {
    const cx = b.w / 2;
    const cy = b.h / 2;
    if (b.content === "typing") {
      return (
        <g>
          <circle className="dot dot-1" cx={cx - 3} cy={cy} r="0.9" fill="rgba(0,0,0,0.4)" />
          <circle className="dot dot-2" cx={cx}     cy={cy} r="0.9" fill="rgba(0,0,0,0.4)" />
          <circle className="dot dot-3" cx={cx + 3} cy={cy} r="0.9" fill="rgba(0,0,0,0.4)" />
        </g>
      );
    }
    return (
      <g stroke="rgba(0,0,0,0.22)" strokeWidth="0.8" strokeLinecap="round">
        <line x1="4" y1={cy - 1.8} x2={b.w - 4} y2={cy - 1.8} />
        <line x1="4" y1={cy + 1.8} x2={b.w - 7} y2={cy + 1.8} />
      </g>
    );
  };

  const tail = (b) => {
    if (b.tail === "right") {
      return <polygon points={`${b.w - 8},${b.h} ${b.w - 5},${b.h + 3} ${b.w - 3},${b.h}`} fill={b.fill} />;
    }
    return <polygon points={`3,${b.h} 6,${b.h + 3} 8,${b.h}`} fill={b.fill} />;
  };

  return (
    <div className="absolute w-full h-full overflow-hidden">
      <svg
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
        viewBox="0 0 100 100"
      >
        <defs>
          <linearGradient id="speechBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={topColor} />
            <stop offset="55%" stopColor={bgColor} />
            <stop offset="100%" stopColor={bottomColor} />
          </linearGradient>
          <filter id="speechShadow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" />
            <feOffset dx="0" dy="0.8" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.35" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <style>{`
            @keyframes speechCycle {
              0%,3%   { transform: translateY(12px) scale(0);    opacity: 0; }
              10%     { transform: translateY(-2px) scale(1.08); opacity: 1; }
              14%     { transform: translateY(0)    scale(1);    opacity: 1; }
              30%     { transform: translateY(-3px) scale(1); }
              50%     { transform: translateY(0)    scale(1); }
              70%     { transform: translateY(-3px) scale(1); }
              90%     { transform: translateY(0)    scale(1);    opacity: 1; }
              100%    { transform: translateY(-10px) scale(0.92); opacity: 0; }
            }
            @keyframes typingDot {
              0%,60%,100% { transform: translateY(0);    opacity: 0.35; }
              30%         { transform: translateY(-1.2px); opacity: 1;    }
            }
            @keyframes reactionFloat {
              0%,8%  { transform: translateY(0)    scale(0.7); opacity: 0;   }
              20%    { transform: translateY(-3px) scale(1);   opacity: 1;   }
              80%    { transform: translateY(-18px) scale(0.9); opacity: 0.5; }
              100%   { transform: translateY(-28px) scale(0.5); opacity: 0;   }
            }
            .speech {
              animation-name: speechCycle;
              animation-timing-function: ease-in-out;
              animation-iteration-count: infinite;
              transform-origin: center;
              transform-box: fill-box;
            }
            .dot-1 { animation: typingDot 1.4s ease-in-out infinite;       }
            .dot-2 { animation: typingDot 1.4s ease-in-out 0.2s infinite;  }
            .dot-3 { animation: typingDot 1.4s ease-in-out 0.4s infinite;  }
            .reaction {
              animation-name: reactionFloat;
              animation-timing-function: ease-out;
              animation-iteration-count: infinite;
              transform-origin: center;
              transform-box: fill-box;
            }
          `}</style>
        </defs>

        <rect x="0" y="0" width="100" height="100" fill="url(#speechBg)" />

        {BUBBLES.map((b, i) => (
          <g
            key={`b-${i}`}
            className="speech"
            style={{ animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s` }}
          >
            <g transform={`translate(${b.x}, ${b.y})`} filter="url(#speechShadow)">
              <rect x="0" y="0" width={b.w} height={b.h} rx={b.rx} fill={b.fill} />
              {tail(b)}
              {innerContent(b)}
            </g>
          </g>
        ))}

        {REACTIONS.map((r, i) => (
          <g
            key={`r-${i}`}
            className="reaction"
            style={{ animationDelay: `${r.delay}s`, animationDuration: `${r.dur}s` }}
            transform={`translate(${r.x}, ${r.y})`}
          >
            {r.kind === "heart" ? (
              <path d={HEART_PATH} fill={r.color} opacity="0.8" />
            ) : (
              <path d={STAR_PATH} fill={r.color} opacity="0.8" />
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};
